
import type { TranslationFix, FixSession, Language } from './types';
import { TranslationSuggestionGenerator } from './suggestionGenerator';
import { TranslationSessionManager } from './sessionManager';
import { TranslationFileGenerator } from './fileGenerator';

export class EnhancedTranslationFixer {
  private static instance: EnhancedTranslationFixer;
  private currentSession: FixSession | null = null;
  
  private suggestionGenerator = new TranslationSuggestionGenerator();
  private sessionManager = new TranslationSessionManager();
  private fileGenerator = new TranslationFileGenerator();

  private constructor() {}

  public static getInstance(): EnhancedTranslationFixer {
    if (!EnhancedTranslationFixer.instance) {
      EnhancedTranslationFixer.instance = new EnhancedTranslationFixer();
    }
    return EnhancedTranslationFixer.instance;
  }

  public generateSmartTranslationSuggestions(keyPath: string, englishValue: string, targetLang: Language): string[] {
    return this.suggestionGenerator.generateSmartTranslationSuggestions(keyPath, englishValue, targetLang);
  }

  public createFixSession(missingKeys: Array<{keyPath: string, language: Language, englishValue: string}>): FixSession {
    const fixes: TranslationFix[] = missingKeys.map(missing => {
      const suggestions = this.generateSmartTranslationSuggestions(
        missing.keyPath, 
        missing.englishValue, 
        missing.language
      );
      
      return {
        keyPath: missing.keyPath,
        language: missing.language,
        currentValue: null,
        suggestedValue: suggestions[0] || missing.englishValue,
        category: missing.keyPath.split('.')[0] || 'general'
      };
    });

    this.currentSession = {
      id: `fix-session-${Date.now()}`,
      timestamp: new Date(),
      fixes,
      appliedFixes: new Map()
    };

    return this.currentSession;
  }

  public applySessionFix(keyPath: string, language: Language, value: string): void {
    if (!this.currentSession) return;
    this.sessionManager.applyFixToSession(this.currentSession, keyPath, language, value);
  }

  public getSessionFix(language: Language, keyPath: string): string | null {
    return this.sessionManager.getSessionFix(this.currentSession, language, keyPath);
  }

  public generateUpdatedTranslationFiles(): {[key: string]: string} {
    if (!this.currentSession) return {};
    return this.fileGenerator.generateUpdatedTranslationFiles(this.currentSession);
  }

  public clearSession(): void {
    this.currentSession = null;
    this.sessionManager.clearStoredFixes();
  }

  public getCurrentSession(): FixSession | null {
    return this.currentSession;
  }
}

// Re-export types for backward compatibility
export type { TranslationFix, FixSession, Language } from './types';
